import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { markWhatsNewTourSeen, setActiveTourItem } from "store/whatsNewSlice";
import whatsNewService from "api/whatsNew/whatsNew.service";
import { AnalyticsEvent } from "utils";

// Arms a feature page's "How it works?" hero tour. The BE decides which pages have a tour via the
// tours map (page_key -> { id, seen }); the button shows only when a tour exists for `pageKey`.
//
//   • hasTour  — render the hero secondaryCta iff true.
//   • openTour — manual open (hero click). Does NOT mark the tour seen (per contract).
//   • auto-open — fires once when the tour has never been seen, and writes seen (BE + local) so it
//     won't re-open. The tour's content is the feed item referenced by tours[pageKey].id.
const useFeatureTour = (pageKey: string) => {
  const dispatch = useAppDispatch();
  const loaded = useAppSelector((s) => s.whatsNew.loaded);
  const tour = useAppSelector((s) => s.whatsNew.tours[pageKey]);
  const items = useAppSelector((s) => s.whatsNew.items);
  const autoOpenedRef = useRef(false);

  const resolveItem = useCallback(() => (tour ? items.find((i) => i.id === tour.id) ?? null : null), [tour, items]);

  // Analytics for the open itself is fired once by FeatureTourModal (single source, any entry point).
  const openTour = useCallback(() => {
    const item = resolveItem();
    if (item) dispatch(setActiveTourItem(item));
  }, [resolveItem, dispatch]);

  useEffect(() => {
    if (!loaded || !tour || tour.seen || autoOpenedRef.current) return;
    const item = resolveItem();
    if (!item) return;

    autoOpenedRef.current = true;
    dispatch(setActiveTourItem(item));
    dispatch(markWhatsNewTourSeen(pageKey));
    whatsNewService.markTourSeen(pageKey).catch(() => undefined);
    AnalyticsEvent("feature_tour_auto_open", "", "impression", { page_key: pageKey });
  }, [loaded, tour, resolveItem, dispatch, pageKey]);

  return { hasTour: !!tour, openTour };
};

export default useFeatureTour;
